/**
 * Utility functions for common mathematical operations.
 */

/**
 * Clamp value within a range.
 * @param {number}  min - The minimum value of the range.
 * @param {number}  max - The maximum value of the range.
 * @param {number}  a   - The value to clamp.
 * @return {number} The clamped value.
 */
const clamp = (min = 0, max = 1, a) => Math.min(max, Math.max(min, a));

/**
 * Linear interpolation (lerp) between two values.
 * @param {number}  x   - The starting value.
 * @param {number}  y   - The ending value.
 * @param {number}  a   - The alpha value (weight).
 * @return {number} The interpolated value.
 */
const lerp = (x, y, a) => x * (1 - a) + y * a;

/**
 * Calculate inverted lerp value within a range.
 * @param {number}  x   - The starting value of the range.
 * @param {number}  y   - The ending value of the range.
 * @param {number}  a   - The value to map within the range.
 * @return {number} The mapped value within the range [0, 1].
 */
const invlerp = (x, y, a) => clamp(0, 1, (a - x) / (y - x));

/**
 * Round number to the specified precision.
 *
 * This function ensures precision unlike Number.prototype.toPrecision()
 * and Number.prototype.toFixed().
 *
 * @param  {number}  number      - The number to round.
 * @param  {number}  [precision] - The number of decimal places.
 * @return {number} The rounded number.
 */
const roundNumber = (number, precision = 2) => {
    return Number.parseFloat(number.toPrecision(precision));
};

/**
 * Map a value from one range to another.
 * @param {number}  min    - The minimum value of the input range.
 * @param {number}  max    - The maximum value of the input range.
 * @param {number}  nmin   - The minimum value of the output range.
 * @param {number}  nmax   - The maximum value of the output range.
 * @param {number}  value  - The value to map.
 * @return {number} The mapped value.
 */
const mapRange = (min, max, nmin, nmax, value) => {
    return ((value - min) / (max - min)) * (nmax - nmin) + nmin;
};

/**
 * Normalize a value within a range to a [0, 1] range.
 * @param {number}  min    - The minimum value of the range.
 * @param {number}  max    - The maximum value of the range.
 * @param {number}  value  - The value to normalize.
 * @return {number} The normalized value within the range [0, 1].
 */
const normalize = (min, max, value) => {
    return clamp(0, 1, (value - min) / (max - min));
};

/**
 * Round a value to a specified number of decimal places.
 * @param {number}  value     - The value to round.
 * @param {number}  decimals  - The number of decimal places.
 * @return {number} The rounded value.
 */
const roundToDecimals = (value, decimals) => {
    const factor = Math.pow(10, decimals);
    return Math.round((value + Number.EPSILON) * factor) / factor;
};

export { clamp, lerp, invlerp, roundNumber, mapRange, normalize, roundToDecimals };
